/**
 * A student's three states, as `/auth/me.class_status` (and the veli dashboard's
 * per-child `class_status`) reports them — the rule lives in birey-backend
 * `utils/individual-student.ts` (`studentClassStatus`):
 *
 *   'placed'     — in a class. The normal UI.
 *   'awaiting'   — kurumlu but in no class yet (every eis ekosistem student on day
 *                  one). The FULL menu, with the explanations below wherever a screen
 *                  needs a class, and Randevularım closed until then.
 *   'individual' — bireysel: no kurum. The bireysel panel, unchanged.
 *
 * ⚠️ Never show 'awaiting' as bireysel. The bireysel panel hides Sınıflarım, which is
 * the one place such a student can join a class with a code.
 */
export type ClassStatus = 'placed' | 'awaiting' | 'individual';

function normalise(raw: unknown, isIndividual: unknown): ClassStatus {
  if (raw === 'placed' || raw === 'awaiting' || raw === 'individual') return raw;
  // A payload from before class_status existed: keep today's two-state behaviour.
  return isIndividual ? 'individual' : 'placed';
}

/** The signed-in student's state; null for every other role. */
export function studentClassStatus(user: unknown): ClassStatus | null {
  const u = user as { role?: string; class_status?: unknown; is_individual?: unknown } | null | undefined;
  if (!u || u.role !== 'student') return null;
  return normalise(u.class_status, u.is_individual);
}

/** A veli's child, from the dashboard's `children[]`. */
export function childClassStatus(child: { classStatus?: unknown; isIndividual?: unknown } | null | undefined): ClassStatus {
  return normalise(child?.classStatus, child?.isIndividual);
}

// ── Copy ────────────────────────────────────────────────────────────────────
// ⚠️ Homework and goals are written PER STUDENT at the moment they are assigned (the
// class roster of that moment) — a student who joins or is placed later gets only what
// is assigned after. The copy must not promise the earlier ones. Randevular and online
// sınavlar are read from the class live, so those do arrive at once.
// One wording for every screen, web and mobile (dijipo-mobile/utils/classStatus.ts
// carries the same strings): say what is missing, who fixes it, and what the student
// can do meanwhile.

export const AWAITING_TITLE = 'Sınıf ataması bekleniyor';

/** Sınıflarım — the one screen where the student can act: join with a code. */
export function awaitingClassesText(kurumName?: string | null): string {
  return `${kurumName || 'Kurumun'} seni henüz bir sınıfa yerleştirmedi. Öğretmeninden sınıf kodu aldıysan buradan katılabilirsin.`;
}

export const AWAITING_APPOINTMENTS_TEXT =
  'Randevular sınıfındaki öğretmenlerle oluşturulur. Kurumun seni bir sınıfa yerleştirdiğinde öğretmenlerin burada listelenecek ve randevu oluşturabileceksin.';

export const AWAITING_ASSIGNMENTS_TEXT =
  'Kurumun seni bir sınıfa yerleştirdikten sonra verilen ödevler burada görünecek.';

/** The profile's "Sınıf" field. */
export const AWAITING_CLASS_VALUE = 'Atama bekleniyor';

/** Veli: the selected child is awaiting a class. */
export function awaitingChildAppointmentsText(childName: string): string {
  return `${childName} henüz bir sınıfa yerleştirilmedi. Randevular sınıfındaki öğretmenlerle oluşturulur; kurum yerleştirmeyi yaptığında açılacak.`;
}

export function awaitingChildAssignmentsText(childName: string): string {
  return `${childName} henüz bir sınıfa yerleştirilmedi. Yerleştirmeden sonra verilen ödevler burada görünecek.`;
}
